/**
 * ContextMenu.tsx
 * 
 * 职责：
 * 1. 提供绘图应用的右键上下文菜单
 * 2. 处理菜单项的点击事件和交互状态
 * 3. 根据当前状态(如是否有选中内容)有条件地启用/禁用菜单项
 * 4. 处理菜单外部点击关闭菜单的逻辑
 * 5. 提供复制、粘贴、删除等常用操作的快捷访问
 * 6. 管理菜单的位置和可见性状态
 * 7. 实现菜单的样式和交互效果
 * 8. 支持元素组合和解除组合操作
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { LineData, ContextMenuState, Point, TextData, CardData, GroupElement } from '../../types';
import { DrawingService } from '../services/DrawingService';
import { TextService } from '../services/TextService';
import { SelectionService } from '../services/SelectionService';
import { CardService } from '../services/CardService';
import { GroupService } from '../services/GroupService';
import { ImageService } from '../services/ImageService';

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  selectionService: SelectionService;
  drawingService: DrawingService;
  textService: TextService;
  cardService: CardService;
  groupService: GroupService;
  imageService: ImageService;
  setCopiedLines: (lines: LineData[]) => void;
  copiedLines: LineData[];
  setCopiedTexts: (texts: TextData[]) => void;
  copiedTexts: TextData[];
  setCopiedCards: (cards: CardData[]) => void;
  copiedCards: CardData[];
  type?: 'line' | 'text' | 'card' | 'empty' | 'group' | 'image'; // 菜单类型
  textIndex?: number; // 文本索引
  lineIndex?: number; // 线条索引
  cardIndex?: number; // 卡片索引
  imageIndex?: number; // 图片索引
  groupId?: string; // 组合ID
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  x,
  y,
  onClose,
  selectionService,
  drawingService,
  textService,
  cardService,
  groupService,
  imageService,
  setCopiedLines,
  copiedLines,
  setCopiedTexts,
  copiedTexts,
  setCopiedCards,
  copiedCards,
  type,
  textIndex,
  lineIndex,
  cardIndex,
  imageIndex,
  groupId
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [showAnnotationInput, setShowAnnotationInput] = useState<boolean>(false);
  const [annotationText, setAnnotationText] = useState<string>('');
  const [position, setPosition] = useState<{x: number, y: number}>({x, y});
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragOffset, setDragOffset] = useState<{x: number, y: number}>({x: 0, y: 0});
  
  // 添加调整后的位置状态，用于处理菜单超出屏幕的情况
  const [adjustedPosition, setAdjustedPosition] = useState<{x: number, y: number}>({x, y});
  
  // 使用ref来跟踪位置初始化状态和初始位置
  const positionInitializedRef = useRef<boolean>(false);
  const initialXRef = useRef<number>(x);
  const initialYRef = useRef<number>(y);
  
  // 辅助变量 - 是否有选中内容
  const hasSelection = selectionService.getSelectedIndices().length > 0 || 
                       selectionService.getSelectedTextIndices().length > 0 || 
                       selectionService.getSelectedCardIndices().length > 0 ||
                       selectionService.getSelectedImageIndices().length > 0;
                       
  // 检查是否有任何可粘贴的内容                     
  const hasCopiedContent = copiedLines.length > 0 || copiedTexts.length > 0 || copiedCards.length > 0;
  
  // 是否在选中区域内右键
  const isRightClickInSelection = type !== 'empty';
  
  // 显示复制和删除选项的条件：有选中内容 且 右键点击位置在选中区域内
  const showCopyDeleteOptions = hasSelection && isRightClickInSelection;
  
  // 检查是否可以组合元素（至少选中两个元素）
  const canGroup = selectionService.canCreateGroup();
  
  // 检查是否在组内右键点击
  const isInGroup = groupId !== undefined && groupId !== null && groupId !== '';
  
  // 检查是否同时选中了组合内元素和组合外元素
  const hasSelectionOutsideGroup = isInGroup && canGroup && selectionService.getSelectedElementsAsGroupElements().length > 
    (groupService.getGroupElements(groupId)?.length || 0);
    
  // 检查是否选中了多个组合
  const hasMultipleGroupsSelected = selectionService.hasMultipleGroupsSelected();
  
  // 添加一个状态来存储文本在屏幕上的位置
  const [textScreenPosition, setTextScreenPosition] = useState<{x: number, y: number} | null>(null);
  // 添加一个状态来存储菜单中心在屏幕上的位置
  const [menuCenterPosition, setMenuCenterPosition] = useState<{x: number, y: number} | null>(null);
  
  // 检查当前是否有内容
  const isContentEmpty = useMemo(() => {
    const linesEmpty = drawingService.getLines().length === 0;
    const textsEmpty = textService.getTexts().length === 0;
    const cardsEmpty = cardService.getCards().length === 0;
    const imagesEmpty = imageService.getImages().length === 0;
    // 所有内容都为空时才返回true
    return linesEmpty && textsEmpty && cardsEmpty && imagesEmpty;
  }, [drawingService, textService, cardService, imageService]);
  
  // 更新菜单中心位置的函数
  const updateMenuCenterPosition = useCallback(() => {
    if (menuRef.current && showAnnotationInput) {
      const menuRect = menuRef.current.getBoundingClientRect();
      setMenuCenterPosition({
        x: menuRect.left + menuRect.width / 2,
        y: menuRect.top + menuRect.height / 2
      });
    }
  }, [showAnnotationInput]);
  
  // 监听窗口大小变化，更新菜单中心位置
  useEffect(() => {
    if (showAnnotationInput) {
      // 初始更新
      updateMenuCenterPosition();
      
      // 添加窗口大小变化监听
      window.addEventListener('resize', updateMenuCenterPosition);
      
      return () => {
        window.removeEventListener('resize', updateMenuCenterPosition);
      };
    }
  }, [showAnnotationInput, updateMenuCenterPosition]);
  
  // 在拖动结束后更新菜单中心位置
  useEffect(() => {
    if (!isDragging && showAnnotationInput) {
      updateMenuCenterPosition();
    }
  }, [isDragging, showAnnotationInput, updateMenuCenterPosition]);
  
  // 检测菜单是否超出屏幕并调整位置
  useEffect(() => {
    if (menuRef.current && !showAnnotationInput) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      
      let newX = x;
      let newY = y;
      
      // 检查是否超出右边界
      if (x + menuRect.width > windowWidth) {
        newX = windowWidth - menuRect.width - 10; // 10px的安全边距
      }
      
      // 检查是否超出下边界
      if (y + menuRect.height > windowHeight) {
        newY = windowHeight - menuRect.height - 10; // 10px的安全边距
      }
      
      // 确保菜单不会超出左边界
      if (newX < 10) {
        newX = 10;
      }
      
      // 确保菜单不会超出上边界
      if (newY < 10) {
        newY = 10;
      }
      
      // 只在初始渲染时设置位置，避免循环更新
      if (!positionInitializedRef.current) {
        setAdjustedPosition({x: newX, y: newY});
        positionInitializedRef.current = true;
      }
    }
  }, [x, y, showAnnotationInput]);
  
  // 获取文本对象在屏幕上的位置
  useEffect(() => {
    if (showAnnotationInput && type === 'text' && textIndex !== undefined) {
      // 获取文本对象
      const texts = textService.getTexts();
      const text = texts[textIndex];
      
      if (text) {
        // 获取画布元素
        const canvas = document.querySelector('canvas');
        if (canvas) {
          // 从文本服务获取视口服务
          const viewportService = (textService as any).viewportService;
          if (viewportService) {
            // 更新文本位置的函数
            const updateTextPosition = () => {
              // 获取文本的尺寸信息
              const textWidth = text.boxWidth || 0;
              const textHeight = text.boxHeight || 0;
              
              // 将整个文本框的中心点的画布坐标转换为屏幕坐标
              // 需要通过计算获取整个文本框的中心
              const ctx = canvas.getContext('2d');
              if (ctx) {
                // 获取创建时的缩放比例
                const creationScale = text.creationScale || 1;
                
                // 设置字体以计算文本尺寸
                ctx.font = `${text.fontSize * (1 / creationScale)}px Arial, sans-serif`;
                
                // 分割文本为多行并计算总高度
                const lines = text.text.split('\n');
                const lineHeight = text.fontSize * (1 / creationScale) * 1.2;
                const totalHeight = lines.length * lineHeight - (lines.length > 1 ? lineHeight * 0.2 : 0);
                
                // 计算垂直中心点偏移量
                // 文本的baseY是第一行文本的基线位置，需要计算到整个文本框中心的偏移
                // 第一行文本的中心是 baseY - (lineHeight / 2)
                // 整个文本框的中心是 (第一行顶部 + totalHeight / 2)
                // 第一行顶部 = baseY - lineHeight
                const verticalOffset = (totalHeight / 2) - (lineHeight / 2);
                
                // 使用文本的baseX（文本水平中心点）和调整后的垂直中心点
                const textCenterX = text.baseX;
                const textCenterY = text.baseY + verticalOffset;
                
                // 转换为屏幕坐标
                const screenPos = viewportService.canvasToClient(textCenterX, textCenterY);
                setTextScreenPosition(screenPos);
              } else {
                // 如果无法获取上下文，使用默认方法
                const screenPos = viewportService.canvasToClient(text.baseX, text.baseY);
                setTextScreenPosition(screenPos);
              }
            };
            
            // 初始更新位置
            updateTextPosition();
            
            // 监听视口变化事件，确保在画布缩放和平移时更新连接线
            const handleViewportChange = () => {
              updateTextPosition();
            };
            
            // 监听文本位置更新事件，当文本被拖动时更新连接线
            const handleTextPositionUpdate = () => {
              updateTextPosition();
            };
            
            // 添加视口变化事件监听器
            document.addEventListener('viewport-changed', handleViewportChange);
            // 添加文本位置更新事件监听器
            document.addEventListener('text-position-updated', handleTextPositionUpdate);
            
            // 清理函数
            return () => {
              document.removeEventListener('viewport-changed', handleViewportChange);
              document.removeEventListener('text-position-updated', handleTextPositionUpdate);
            };
          }
        }
      }
    }
  }, [showAnnotationInput, type, textIndex, textService]);
  
  // 点击外部关闭菜单
  useEffect(() => {
    let isMouseDown = false;

    const handleMouseDown = (event: MouseEvent) => {
      // 如果是注释输入状态，不要在Canvas上的点击时关闭编辑框
      if (showAnnotationInput) {
        const canvasElement = document.querySelector('canvas');
        // 点击的是注释框外部，但不是Canvas
        if (menuRef.current && !menuRef.current.contains(event.target as Node) && 
            !(canvasElement && canvasElement.contains(event.target as Node))) {
          isMouseDown = true;
        }
      } else {
        // 如果点击的是菜单外部
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          // 立即关闭菜单，不等待mouseup
          onClose();
          isMouseDown = true;
        }
      }
    };

    const handleMouseUp = (event: MouseEvent) => {
      // 如果是注释输入状态，不要在Canvas上的点击时关闭编辑框
      if (showAnnotationInput) {
        const canvasElement = document.querySelector('canvas');
        // 只有当鼠标按下和释放都在菜单外部且不在Canvas上时才关闭
        if (isMouseDown && menuRef.current && !menuRef.current.contains(event.target as Node) && 
            !(canvasElement && canvasElement.contains(event.target as Node))) {
          onClose();
        }
      }
      // 普通菜单状态下不需要在mouseup时处理，因为已经在mousedown时关闭了
      isMouseDown = false;
    };
    
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [onClose, showAnnotationInput]);
  
  // 设置初始位置
  useEffect(() => {
    // 只在组件初次挂载和x,y初始值改变时设置位置
    // 使用ref来跟踪是否已经初始化过位置
    if (!isDragging && (!positionInitializedRef.current || x !== initialXRef.current || y !== initialYRef.current)) {
      setPosition({x, y});
      positionInitializedRef.current = true;
      initialXRef.current = x;
      initialYRef.current = y;
    }

    // 为注释编辑框添加视口变化事件监听器，确保在画布缩放和平移时不移动
    if (showAnnotationInput) {
      const handleViewportChange = () => {
        // 保持注释编辑框位置不变，忽略视口变化
        if (positionInitializedRef.current) {
          // 不更新位置，保持屏幕位置固定
          return;
        }
      };

      document.addEventListener('viewport-changed', handleViewportChange);
      
      return () => {
        document.removeEventListener('viewport-changed', handleViewportChange);
      };
    }
  }, [x, y, isDragging, showAnnotationInput]);
  
  // 拖动功能
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      // 计算新位置
      const newPosition = {
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      };
      
      // 更新位置状态
      setPosition(newPosition);
      
      // 在拖动过程中实时更新菜单中心位置
      requestAnimationFrame(() => {
        updateMenuCenterPosition();
      });
    };
    
    const handleMouseUp = () => {
      // 结束拖动状态，但不重置位置
      setIsDragging(false);
      
      // 在拖动结束时，确保位置保持不变
      positionInitializedRef.current = true;
      
      // 更新菜单中心位置
      updateMenuCenterPosition();
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, updateMenuCenterPosition]);
  
  // 计算连接线的控制点，创建一条弯曲的贝塞尔曲线
  const getConnectionPath = () => {
    if (!textScreenPosition || !menuCenterPosition) return '';
    
    // 文本中心位置和菜单中心位置
    const { x: textX, y: textY } = textScreenPosition;
    const { x: menuX, y: menuY } = menuCenterPosition;
    
    // 获取文本服务和当前文本对象
    if (type === 'text' && textIndex !== undefined) {
      const texts = textService.getTexts();
      const text = texts[textIndex];
      
      if (text && document.querySelector('canvas')) {
        const canvas = document.querySelector('canvas') as HTMLCanvasElement;
        const ctx = canvas.getContext('2d');
        const viewportService = (textService as any).viewportService;
        
        if (ctx && viewportService) {
          // 计算文本框的边界 - 使用与SelectionService.getTextBoundingBox相同的逻辑
          const creationScale = text.creationScale || 1;
          
          // 估算文本宽度和高度
          let textWidth = 0;
          
          // 设置字体大小并考虑创建比例的影响
          const scaledFontSize = text.fontSize * (1 / creationScale);
          ctx.font = `${scaledFontSize}px Arial`;
          const lines = text.text.split('\n');
          
          // 找出最宽的行
          for (const line of lines) {
            const metrics = ctx.measureText(line);
            textWidth = Math.max(textWidth, metrics.width);
          }
          
          // 计算总高度 - 使用应用了创建比例的行高
          const lineHeight = scaledFontSize * 1.2;
          const textHeight = lines.length * lineHeight - (lines.length > 1 ? lineHeight * 0.2 : 0);
          
          // 计算整个文本块的中心点
          const halfWidth = textWidth / 2;
          
          // 对象的基础边界 - 从第一行文字的中心点开始计算
          let bounds = {
            minX: text.baseX - halfWidth,
            minY: text.baseY - (scaledFontSize / 2), // 从第一行文字的中心点向上偏移半个字体大小
            maxX: text.baseX + halfWidth,
            maxY: text.baseY + textHeight - (scaledFontSize / 2) // 向下延伸整个文本高度，减去半个字体大小
          };
          
          // 包含边距
          const currentScale = viewportService.getScale();
          const margin = 5 / currentScale; // 5像素的边距，随缩放调整
          
          bounds.minX -= margin;
          bounds.minY -= margin;
          bounds.maxX += margin;
          bounds.maxY += margin;
          
          // 应用额外的偏移量
          if (text.offsetX || text.offsetY) {
            const offsetX = (text.offsetX || 0) / creationScale;
            const offsetY = (text.offsetY || 0) / creationScale;
            
            bounds.minX += offsetX;
            bounds.minY += offsetY;
            bounds.maxX += offsetX;
            bounds.maxY += offsetY;
          }
          
          // 将文本框边界的四个角转换为屏幕坐标
          const topLeft = viewportService.canvasToClient(bounds.minX, bounds.minY);
          const topRight = viewportService.canvasToClient(bounds.maxX, bounds.minY);
          const bottomLeft = viewportService.canvasToClient(bounds.minX, bounds.maxY);
          const bottomRight = viewportService.canvasToClient(bounds.maxX, bounds.maxY);
          
          // 计算文本中心到注释编辑框中心的方向向量
          const dx = menuX - textX;
          const dy = menuY - textY;
          
          // 根据方向向量确定连线应该从文本框的哪条边开始
          // 寻找线段与文本框边界的交点
          let intersectionX = textX;
          let intersectionY = textY;
          
          // 计算交点
          // 根据方向的主要方向，选择与相应边界的交点
          if (Math.abs(dx) > Math.abs(dy)) {
            // 水平方向为主
            if (dx > 0) {
              // 向右，从右边界出发
              intersectionX = topRight.x;
              // 在右边界上计算y坐标
              const t = (intersectionX - textX) / dx;
              intersectionY = textY + t * dy;
              
              // 确保y坐标在上下边界之间
              intersectionY = Math.max(topRight.y, Math.min(bottomRight.y, intersectionY));
            } else {
              // 向左，从左边界出发
              intersectionX = topLeft.x;
              // 在左边界上计算y坐标
              const t = (intersectionX - textX) / dx;
              intersectionY = textY + t * dy;
              
              // 确保y坐标在上下边界之间
              intersectionY = Math.max(topLeft.y, Math.min(bottomLeft.y, intersectionY));
            }
          } else {
            // 垂直方向为主
            if (dy > 0) {
              // 向下，从下边界出发
              intersectionY = bottomLeft.y;
              // 在下边界上计算x坐标
              const t = (intersectionY - textY) / dy;
              intersectionX = textX + t * dx;
              
              // 确保x坐标在左右边界之间
              intersectionX = Math.max(bottomLeft.x, Math.min(bottomRight.x, intersectionX));
            } else {
              // 向上，从上边界出发
              intersectionY = topLeft.y;
              // 在上边界上计算x坐标
              const t = (intersectionY - textY) / dy;
              intersectionX = textX + t * dx;
              
              // 确保x坐标在左右边界之间
              intersectionX = Math.max(topLeft.x, Math.min(topRight.x, intersectionX));
            }
          }
          
          // 使用计算出的交点作为连线起点
          // 计算控制点（贝塞尔曲线的弯曲程度）
          const controlPointX1 = intersectionX + dx / 3;
          const controlPointY1 = intersectionY + dy / 3;
          
          const controlPointX2 = menuX - dx / 3;
          const controlPointY2 = menuY - dy / 3;
          
          // 生成贝塞尔曲线路径 - 从文本框边界到注释编辑框中心
          return `M ${intersectionX} ${intersectionY} C ${controlPointX1} ${controlPointY1}, ${controlPointX2} ${controlPointY2}, ${menuX} ${menuY}`;
        }
      }
    }
    
    // 如果无法计算交点，退回到简单连线
    // 计算控制点（贝塞尔曲线的弯曲程度）
    const controlPointX1 = textX;
    const controlPointY1 = textY + (menuY - textY) / 3;
    
    const controlPointX2 = menuX;
    const controlPointY2 = menuY - (menuY - textY) / 3;
    
    // 生成贝塞尔曲线路径
    return `M ${textX} ${textY} C ${controlPointX1} ${controlPointY1}, ${controlPointX2} ${controlPointY2}, ${menuX} ${menuY}`;
  };
  
  // 启动拖动
  const handleStartDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    // 防止文本选择和默认事件
    e.preventDefault();
    // 阻止事件传播到Canvas
    e.stopPropagation();
    
    // 获取当前菜单位置和尺寸
    const rect = menuRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    // 计算鼠标与菜单左上角的相对位置
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    // 存储这个偏移量
    setDragOffset({
      x: offsetX,
      y: offsetY
    });
    
    // 开始拖动状态
    setIsDragging(true);
  };
  
  // 复制
  const handleCopy = () => {
    // 获取选中的线条并过滤掉无效线条
    const selectedIndices = selectionService.getSelectedIndices();
    const allLines = drawingService.getLines();
    const selectedLines = selectedIndices
      .map(index => allLines[index])
      .filter(line => line && line.points && Array.isArray(line.points) && line.points.length > 0);
    setCopiedLines([...selectedLines]);
    
    // 获取选中的文本并过滤掉无效文本
    const selectedTextIndices = selectionService.getSelectedTextIndices();
    const allTexts = textService.getTexts();
    const selectedTexts = selectedTextIndices
      .map(index => allTexts[index])
      .filter(text => text && typeof text.baseX === 'number' && typeof text.baseY === 'number');
    setCopiedTexts([...selectedTexts]);
    
    // 获取选中的卡片并过滤掉无效卡片
    const selectedCardIndices = selectionService.getSelectedCardIndices();
    const allCards = cardService.getCards();
    const selectedCards = selectedCardIndices
      .map(index => allCards[index])
      .filter(card => card && typeof card.x === 'number' && typeof card.y === 'number' &&
                      typeof card.width === 'number' && typeof card.height === 'number');
    setCopiedCards([...selectedCards]);
    
    // 存储选中元素的组合信息
    const groupedElements: Record<string, GroupElement[]> = {};
    
    // 查找线条的组合
    selectedIndices.forEach((index, arrayIndex) => {
      const groupId = groupService.isElementInAnyGroup('line', index);
      if (groupId) {
        if (!groupedElements[groupId]) {
          groupedElements[groupId] = [];
        }
        groupedElements[groupId].push({ type: 'line', index: arrayIndex });
      }
    });
    
    // 查找文本的组合
    selectedTextIndices.forEach((index, arrayIndex) => {
      const groupId = groupService.isElementInAnyGroup('text', index);
      if (groupId) {
        if (!groupedElements[groupId]) {
          groupedElements[groupId] = [];
        }
        groupedElements[groupId].push({ type: 'text', index: arrayIndex });
      }
    });
    
    // 查找卡片的组合
    selectedCardIndices.forEach((index, arrayIndex) => {
      const groupId = groupService.isElementInAnyGroup('card', index);
      if (groupId) {
        if (!groupedElements[groupId]) {
          groupedElements[groupId] = [];
        }
        groupedElements[groupId].push({ type: 'card', index: arrayIndex });
      }
    });
    
    // 将组合信息存储在window对象中，以便在粘贴时使用
    // @ts-ignore
    window.__copiedGroups = groupedElements;
    
    onClose();
  };
  
  // 删除
  const handleDelete = () => {
    if (hasSelection) {
      selectionService.deleteSelectedContent(drawingService, textService, cardService, imageService);
      
      // 强制立即重绘以确保删除的元素立即消失
      try {
        // 1. 尝试通过DOM事件触发Canvas渲染
        const canvasElement = document.querySelector('canvas');
        if (canvasElement) {
          const renderEvent = new CustomEvent('render-required');
          canvasElement.dispatchEvent(renderEvent);
        }
        
        // 2. 通过document级别事件触发渲染
        document.dispatchEvent(new CustomEvent('render-required'));
        
        // 3. 使用微任务确保在当前执行上下文之后立即执行
        Promise.resolve().then(() => {
          document.dispatchEvent(new CustomEvent('render-required'));
        });
        
        // 4. 使用宏任务确保在微任务之后执行
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('render-required'));
          
          // 再次尝试通过Canvas元素触发
          const canvas = document.querySelector('canvas');
          if (canvas) {
            canvas.dispatchEvent(new CustomEvent('render-required'));
          }
        }, 0);
        
        // 5. 使用requestAnimationFrame确保在下一帧渲染
        requestAnimationFrame(() => {
          document.dispatchEvent(new CustomEvent('render-required'));
        });
      } catch (error) {
        // 渲染触发失败，但不中断执行
      }
    }
    
    onClose();
  };
  
  // 全部选中
  const handleSelectAll = () => {
    selectionService.selectAll(
      drawingService.getLines(),
      textService.getTexts(),
      cardService.getCards(),
      imageService.getImages()
    );
    
    // 强制立即重绘以显示选择框
    try {
      // 1. 尝试通过DOM事件触发Canvas渲染
      const canvasElement = document.querySelector('canvas');
      if (canvasElement) {
        const renderEvent = new CustomEvent('render-required');
        canvasElement.dispatchEvent(renderEvent);
      }
      
      // 2. 通过document级别事件触发渲染
      document.dispatchEvent(new CustomEvent('render-required'));
      
      // 3. 使用微任务确保在当前执行上下文之后立即执行
      Promise.resolve().then(() => {
        document.dispatchEvent(new CustomEvent('render-required'));
      });
      
      // 4. 使用宏任务确保在微任务之后执行
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('render-required'));
        
        // 再次尝试通过Canvas元素触发
        const canvas = document.querySelector('canvas');
        if (canvas) {
          canvas.dispatchEvent(new CustomEvent('render-required'));
        }
      }, 0);
      
      // 5. 使用requestAnimationFrame确保在下一帧渲染
      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('render-required'));
      });
    } catch (error) {
      // 渲染触发失败，但不中断执行
    }
    
    onClose();
  };
  
  // 粘贴
  const handlePaste = () => {
    // 获取视口服务
    const viewportService = (textService as any).viewportService;
    if (!viewportService) return;
    
    // 将屏幕坐标转换为画布坐标
    const canvasPoint = viewportService.clientToCanvas(x, y);
    if (!canvasPoint) return;
    
    // 先关闭菜单，避免菜单阻碍视图
    onClose();
    
    // 计算偏移量时，需要考虑所有被复制的元素
    let minX = Number.MAX_VALUE;
    let minY = Number.MAX_VALUE;
    let maxX = -Number.MAX_VALUE;
    let maxY = -Number.MAX_VALUE;
    
    // 分析线条边界
    if (copiedLines && copiedLines.length > 0) {
      copiedLines.forEach((line: LineData) => {
        if (line && line.points && Array.isArray(line.points)) {
          line.points.forEach((point: Point) => {
            if (point && typeof point.x === 'number' && typeof point.y === 'number') {
              minX = Math.min(minX, point.x);
              minY = Math.min(minY, point.y);
              maxX = Math.max(maxX, point.x);
              maxY = Math.max(maxY, point.y);
            }
          });
        }
      });
    }
    
    // 分析文本边界
    if (copiedTexts && copiedTexts.length > 0) {
      copiedTexts.forEach((text: TextData) => {
        // 确保文本对象存在且具有必要的属性
        if (text && typeof text.baseX === 'number' && typeof text.baseY === 'number') {
          // 简单地使用文本基点坐标
          minX = Math.min(minX, text.baseX);
          minY = Math.min(minY, text.baseY);
          maxX = Math.max(maxX, text.baseX);
          maxY = Math.max(maxY, text.baseY);
        }
      });
    }
    
    // 分析卡片边界
    if (copiedCards && copiedCards.length > 0) {
      copiedCards.forEach((card: CardData) => {
        if (card && typeof card.x === 'number' && typeof card.y === 'number' && 
            typeof card.width === 'number' && typeof card.height === 'number') {
          minX = Math.min(minX, card.x);
          minY = Math.min(minY, card.y);
          maxX = Math.max(maxX, card.x + card.width);
          maxY = Math.max(maxY, card.y + card.height);
        }
      });
    }
    
    // 如果没有复制内容，直接返回
    if (minX === Number.MAX_VALUE) return;
    
    // 计算中心点
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    
    // 计算要移动的距离
    const offsetX = canvasPoint.x - centerX;
    const offsetY = canvasPoint.y - centerY;
    
    // 跟踪新添加的元素索引，用于之后的选择
    const newLineIndices: number[] = [];
    const newTextIndices: number[] = [];
    const newCardIndices: number[] = [];
    
    // 获取复制的组合信息
    // @ts-ignore
    const copiedGroups: Record<string, GroupElement[]> = window.__copiedGroups || {};
    
    // 创建映射，将旧的复制索引映射到新的粘贴后索引
    const indexMapping: Record<string, {oldIndex: number, newIndex: number}[]> = {
      'line': [],
      'text': [],
      'card': []
    };
    
    // 粘贴线条
    if (copiedLines && copiedLines.length > 0) {
      // 过滤掉无效的线条
      const validLines = copiedLines.filter(
        (line) => line && line.points && Array.isArray(line.points) && line.points.length > 0
      );
      
      // 创建新的线条，并应用偏移量
      const newLines = validLines.map((line: LineData) => {
        const newPath = new Path2D();
        // 创建新的点数组，并应用偏移量
        const newPoints = line.points.map((point: Point) => ({
          x: point.x + offsetX,
          y: point.y + offsetY
        }));
        
        // 创建路径
        if (newPoints.length > 0) {
          newPath.moveTo(newPoints[0].x, newPoints[0].y);
          for (let i = 1; i < newPoints.length; i++) {
            newPath.lineTo(newPoints[i].x, newPoints[i].y);
          }
        }
        
        // 返回新的线条对象
        return {
          ...line,
          path: newPath,
          points: newPoints
        };
      });
      
      // 将新线条添加到现有内容中
      const currentLines = drawingService.getLines();
      drawingService.setLines([...currentLines, ...newLines]);
      
      // 记录新添加的线条索引
      for (let i = 0; i < newLines.length; i++) {
        const newIndex = currentLines.length + i;
        newLineIndices.push(newIndex);
        // 存储索引映射关系
        indexMapping['line'].push({ oldIndex: i, newIndex });
      }
    }
    
    // 粘贴文本
    if (copiedTexts && copiedTexts.length > 0) {
      // 过滤掉无效的文本对象
      const validTexts = copiedTexts.filter(
        (text) => text && typeof text.baseX === 'number' && typeof text.baseY === 'number'
      );
      
      // 创建新的文本对象，并应用偏移量
      const newTexts = validTexts.map((text: TextData) => {
        return {
          ...text,
          baseX: text.baseX + offsetX,
          baseY: text.baseY + offsetY,
          isEditing: false // 确保粘贴后文本不处于编辑状态
        };
      });
      
      // 将新文本添加到现有内容中
      const currentTexts = textService.getTexts();
      textService.setTexts([...currentTexts, ...newTexts]);
      
      // 记录新添加的文本索引
      for (let i = 0; i < newTexts.length; i++) {
        const newIndex = currentTexts.length + i;
        newTextIndices.push(newIndex);
        // 存储索引映射关系
        indexMapping['text'].push({ oldIndex: i, newIndex });
      }
    }
    
    // 粘贴卡片
    if (copiedCards && copiedCards.length > 0) {
      // 过滤掉无效的卡片
      const validCards = copiedCards.filter(
        (card) => card && typeof card.x === 'number' && typeof card.y === 'number' && 
                  typeof card.width === 'number' && typeof card.height === 'number'
      );
      
      // 创建新的卡片对象，并应用偏移量
      const newCards = validCards.map((card: CardData) => {
        return {
          ...card,
          id: `card-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // 创建新ID
          x: card.x + offsetX,
          y: card.y + offsetY,
          isEditing: false // 确保粘贴后卡片不处于编辑状态
        };
      });
      
      // 将新卡片添加到现有内容中
      const currentCards = cardService.getCards();
      cardService.setCards([...currentCards, ...newCards]);
      
      // 记录新添加的卡片索引
      for (let i = 0; i < newCards.length; i++) {
        const newIndex = currentCards.length + i;
        newCardIndices.push(newIndex);
        // 存储索引映射关系
        indexMapping['card'].push({ oldIndex: i, newIndex });
      }
    }
    
    // 重建组合关系
    if (Object.keys(copiedGroups).length > 0) {
      // 为每个组创建新的组
      Object.entries(copiedGroups).forEach(([_, elements]) => {
        // 创建新的组元素集合，将原始索引映射到新索引
        const newGroupElements: GroupElement[] = [];
        const groupElements = elements as GroupElement[];
        
        groupElements.forEach(element => {
          const { type, index: oldIndex } = element;
          // 查找对应的新索引
          const mapping = indexMapping[type].find(m => m.oldIndex === oldIndex);
          if (mapping) {
            newGroupElements.push({
              type,
              index: mapping.newIndex
            });
          }
        });
        
        // 只有当有至少两个元素时才创建组
        if (newGroupElements.length >= 2) {
          groupService.createGroup(newGroupElements);
        }
      });
    }
    
    // 选中所有新添加的元素
    selectionService.setSelectedIndices(newLineIndices);
    selectionService.setSelectedTextIndices(newTextIndices);
    selectionService.setSelectedCardIndices(newCardIndices);
    
    // 强制立即渲染
    try {
      // 1. 尝试通过DOM事件触发Canvas渲染
      const canvasElement = document.querySelector('canvas');
      if (canvasElement) {
        const renderEvent = new CustomEvent('render-required');
        canvasElement.dispatchEvent(renderEvent);
      }
      
      // 2. 通过document级别事件触发渲染
      document.dispatchEvent(new CustomEvent('render-required'));
      
      // 3. 在多个微任务和宏任务中触发渲染，确保能够被捕获
      Promise.resolve().then(() => {
        document.dispatchEvent(new CustomEvent('render-required'));
      });
      
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('render-required'));
        
        // 再次尝试通过Canvas元素触发
        const canvas = document.querySelector('canvas');
        if (canvas) {
          canvas.dispatchEvent(new CustomEvent('render-required'));
        }
      }, 0);
      
      // 4. 使用requestAnimationFrame确保在下一帧渲染
      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('render-required'));
      });
    } catch (error) {
      // 渲染触发失败，但不中断执行
    }
  };
  
  // 添加注释
  const handleAddAnnotation = () => {
    if (type === 'text' && textIndex !== undefined) {
      // 获取文本对象，如果已有注释，则预填充
      const texts = textService.getTexts();
      if (texts[textIndex] && texts[textIndex].hasAnnotation && texts[textIndex].annotation) {
        // 使用现有注释内容初始化
        setAnnotationText(texts[textIndex].annotation || '');
      } else {
        // 如果没有现有注释，设置为空
        setAnnotationText('');
      }
      
      // 重置位置初始化标志，以便正确设置初始位置
      positionInitializedRef.current = false;
      setPosition({x, y});
      
      setShowAnnotationInput(true);
      
      // 使用setTimeout确保DOM已更新
      setTimeout(() => {
        updateMenuCenterPosition();
      }, 0);
    }
  };
  
  // 保存注释
  const handleSaveAnnotation = () => {
    if (type === 'text' && textIndex !== undefined) {
      const texts = textService.getTexts();
      if (texts[textIndex]) {
        // 如果注释内容为空，则视为删除注释
        if (annotationText.trim() === '') {
          texts[textIndex].annotation = '';
          texts[textIndex].hasAnnotation = false;
        } else {
          // 更新文本的注释
          texts[textIndex].annotation = annotationText;
          texts[textIndex].hasAnnotation = true;
        }
        
        // 触发重新渲染
        const event = new CustomEvent('render-required');
        document.dispatchEvent(event);
        
        // 触发工具操作完成事件，自动保存当前内容状态
        document.dispatchEvent(new CustomEvent('tool-operation-complete'));
      }
      
      setShowAnnotationInput(false);
      onClose();
    }
  };
  
  // 在菜单渲染后初始化中心位置
  useEffect(() => {
    if (showAnnotationInput && menuRef.current) {
      // 使用setTimeout确保DOM已完全渲染
      setTimeout(() => {
        updateMenuCenterPosition();
      }, 50);
    }
  }, [showAnnotationInput, updateMenuCenterPosition]);
  
  // 处理组合元素
  const handleGroup = () => {
    // 检查是否选中了多个组合
    if (hasMultipleGroupsSelected) {
      // 获取所有选中的元素
      const groupElements = selectionService.getSelectedElementsAsGroupElements();
      
      // 获取所有选中的组ID
      const selectedGroupIds = selectionService.getSelectedGroupIds();
      
      // 创建一个新组合
      const newGroupId = groupService.createGroup(groupElements);
      
      // 删除原有组合
      if (newGroupId) {
        selectedGroupIds.forEach(id => {
          groupService.ungroup(id);
        });
        
        // 设置新的选中组ID
        selectionService.setSelectedGroupId(newGroupId);
      }
      
      // 触发工具操作完成事件
      document.dispatchEvent(new CustomEvent('tool-operation-complete'));
      
      // 强制立即重绘以显示组合框
      try {
        // 1. 尝试通过DOM事件触发Canvas渲染
        const canvasElement = document.querySelector('canvas');
        if (canvasElement) {
          canvasElement.dispatchEvent(new CustomEvent('render-required'));
        }
        
        // 2. 通过document级别事件触发渲染
        document.dispatchEvent(new CustomEvent('render-required'));
        
        // 3. 使用微任务确保在当前执行上下文之后立即执行
        Promise.resolve().then(() => {
          document.dispatchEvent(new CustomEvent('render-required'));
        });
        
        // 4. 使用requestAnimationFrame确保在下一帧渲染
        requestAnimationFrame(() => {
          document.dispatchEvent(new CustomEvent('render-required'));
        });
      } catch (error) {
        // 渲染触发失败，但不中断执行
      }
    }
    // 如果已有组合ID且同时选中了组外元素，将组外元素添加到现有组合中
    else if (groupId && hasSelectionOutsideGroup) {
      // 获取当前组中的元素
      const groupElements = groupService.getGroupElements(groupId);
      
      // 获取所有选中的元素
      const selectedElements = selectionService.getSelectedElementsAsGroupElements();
      
      // 找出不在当前组中的元素
      const elementsToAdd = selectedElements.filter(selectedEl => {
        return !groupElements.some(groupEl => 
          groupEl.type === selectedEl.type && groupEl.index === selectedEl.index
        );
      });
      
      // 将不在组中的元素添加到组中
      if (elementsToAdd.length > 0) {
        groupService.addElementsToGroup(groupId, elementsToAdd);
        
        // 触发工具操作完成事件，确保保存组合状态
        document.dispatchEvent(new CustomEvent('tool-operation-complete'));
        
        // 强制立即重绘以显示更新后的组合框
        try {
          document.dispatchEvent(new CustomEvent('render-required'));
          requestAnimationFrame(() => {
            document.dispatchEvent(new CustomEvent('render-required'));
          });
        } catch (error) {
          // 渲染触发失败，但不中断执行
        }
      }
    } else {
      // 创建新组合
      // 获取当前选中的元素
      const groupElements = selectionService.getSelectedElementsAsGroupElements();
      
      // 创建组合
      const newGroupId = groupService.createGroup(groupElements);
      
      // 更新选中的组ID
      if (newGroupId) {
        selectionService.setSelectedGroupId(newGroupId);
      }
      
      // 触发工具操作完成事件，确保保存组合状态
      document.dispatchEvent(new CustomEvent('tool-operation-complete'));
      
      // 强制立即重绘以显示组合框
      try {
        // 1. 尝试通过DOM事件触发Canvas渲染
        const canvasElement = document.querySelector('canvas');
        if (canvasElement) {
          canvasElement.dispatchEvent(new CustomEvent('render-required'));
        }
        
        // 2. 通过document级别事件触发渲染
        document.dispatchEvent(new CustomEvent('render-required'));
        
        // 3. 使用微任务确保在当前执行上下文之后立即执行
        Promise.resolve().then(() => {
          document.dispatchEvent(new CustomEvent('render-required'));
        });
        
        // 4. 使用requestAnimationFrame确保在下一帧渲染
        requestAnimationFrame(() => {
          document.dispatchEvent(new CustomEvent('render-required'));
        });
      } catch (error) {
        // 渲染触发失败，但不中断执行
      }
    }
    
    onClose();
  };
  
  // 处理解除组合
  const handleUngroup = () => {
    if (groupId) {
      // 解除组合
      groupService.ungroup(groupId);
      
      // 清除选中的组ID
      selectionService.setSelectedGroupId(null);
    }
    
    // 触发工具操作完成事件，确保保存组合状态
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
    
    // 强制立即重绘以显示解除组合后的多个选择框
    try {
      // 1. 尝试通过DOM事件触发Canvas渲染
      const canvasElement = document.querySelector('canvas');
      if (canvasElement) {
        canvasElement.dispatchEvent(new CustomEvent('render-required'));
      }
      
      // 2. 通过document级别事件触发渲染
      document.dispatchEvent(new CustomEvent('render-required'));
      
      // 3. 使用微任务确保在当前执行上下文之后立即执行
      Promise.resolve().then(() => {
        document.dispatchEvent(new CustomEvent('render-required'));
      });
      
      // 4. 使用requestAnimationFrame确保在下一帧渲染
      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('render-required'));
      });
    } catch (error) {
      // 渲染触发失败，但不中断执行
    }
    
    onClose();
  };
  
  // 当x或y变化时重置位置初始化标志
  useEffect(() => {
    // 如果位置改变，重置初始化标志
    if (x !== initialXRef.current || y !== initialYRef.current) {
      positionInitializedRef.current = false;
      initialXRef.current = x;
      initialYRef.current = y;
      // 立即更新调整后的位置为新的初始位置，避免闪烁
      setAdjustedPosition({x, y});
    }
  }, [x, y]);
  
  // 渲染输入框
  if (showAnnotationInput) {
    const connectionPath = getConnectionPath();
    
    return (
      <>
        {/* 连接线 */}
        {textScreenPosition && menuCenterPosition && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 999
            }}
          >
            <svg
              width="100%"
              height="100%"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none'
              }}
            >
              {/* 使用贝塞尔曲线绘制更美观的连接线 */}
              <path
                d={connectionPath}
                fill="none"
                stroke="#4a90e2"
                strokeWidth="2"
                strokeDasharray="5,3"
                strokeLinecap="round"
                opacity="0.7"
              />
              {/* 文本端的小圆点 - 改为位于文本框边缘 */}
              {connectionPath && (
                <circle
                  // 从路径中提取起点坐标
                  cx={connectionPath.split(' ')[1]}
                  cy={connectionPath.split(' ')[2]}
                  r="4"
                  fill="#4a90e2"
                  opacity="0.9"
                />
              )}
              {/* 编辑框端的小圆点 */}
              <circle
                cx={menuCenterPosition.x}
                cy={menuCenterPosition.y}
                r="3"
                fill="#4a90e2"
                opacity="0.7"
              />
            </svg>
          </div>
        )}
        
        <div
          ref={menuRef}
          className="annotation-input-container"
          onClick={(e) => {
            // 阻止事件传播，防止触发Canvas上的点击事件
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            // 阻止事件传播，防止触发Canvas上的点击事件
            // 但不要阻止dragHeader的事件处理
            if (e.target !== e.currentTarget && 
                !(e.target as HTMLElement).className?.includes('drag-header')) {
              e.stopPropagation();
            }
          }}
          style={{
            position: 'fixed',
            top: `${position.y}px`,
            left: `${position.x}px`,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: isDragging 
              ? '0 5px 15px rgba(0, 0, 0, 0.2)' 
              : '0 2px 10px rgba(0, 0, 0, 0.1)', // 拖动时显示更强的阴影
            zIndex: 1000,
            padding: '12px',
            minWidth: '250px',
            userSelect: 'none', // 防止拖动时选择文本
            transition: isDragging ? 'none' : 'box-shadow 0.2s ease' // 拖动时禁用过渡效果
          }}
        >
          <div 
            className="drag-header"
            style={{ 
              marginBottom: '8px', 
              fontWeight: 'bold',
              cursor: isDragging ? 'grabbing' : 'grab', // 根据拖动状态改变光标
              padding: '4px 0',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              userSelect: 'none'
            }}
            onMouseDown={handleStartDrag}
          >
            <span>
              {type === 'text' && textIndex !== undefined && textService.getTexts()[textIndex]?.hasAnnotation 
                ? '编辑注释' 
                : '添加注释'}
            </span>
            <span style={{ fontSize: '12px', color: '#999' }}>可拖动</span>
          </div>
          <textarea
            autoFocus
            value={annotationText}
            onChange={(e) => setAnnotationText(e.target.value)}
            onClick={(e) => {
              // 阻止事件传播，防止触发Canvas上的点击事件
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              // 阻止事件传播，防止触发Canvas上的点击事件
              e.stopPropagation();
            }}
            data-annotation-editor="true"
            ref={(textarea) => {
              // 当文本框被渲染后，将光标定位到内容末尾
              if (textarea) {
                // 使用setTimeout确保在下一个渲染周期执行，避免React警告
                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(annotationText.length, annotationText.length);
                }, 0);
              }
            }}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              resize: 'vertical',
              marginBottom: '8px',
              userSelect: 'text' // 确保文本区域可以选择文本
            }}
            placeholder="请输入注释内容..."
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button 
              onClick={(e) => {
                // 阻止事件传播
                e.stopPropagation();
                setShowAnnotationInput(false);
                onClose();
              }}
              style={{
                padding: '6px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f5f5f5',
                cursor: 'pointer'
              }}
            >
              取消
            </button>
            <button 
              onClick={(e) => {
                // 阻止事件传播
                e.stopPropagation();
                handleSaveAnnotation();
              }}
              style={{
                padding: '6px 12px',
                border: '1px solid #4a90e2',
                borderRadius: '4px',
                backgroundColor: '#4a90e2',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              保存
            </button>
          </div>
        </div>
      </>
    );
  }
  
  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: adjustedPosition.y,
        left: adjustedPosition.x,
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        padding: '4px 0'
      }}
    >
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {/* 复制选项：仅在有选择内容且右键点击在选中区域内时可用 */}
        {showCopyDeleteOptions && (
          <li
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              transition: 'background-color 0.2s',
              userSelect: 'none'
            }}
            onClick={handleCopy}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            复制
          </li>
        )}
        
        {/* 粘贴选项：始终显示，但根据是否有复制内容决定是否可用 */}
        <li
          style={{
            padding: '8px 16px',
            cursor: hasCopiedContent ? 'pointer' : 'not-allowed',
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s',
            userSelect: 'none',
            color: hasCopiedContent ? 'inherit' : '#ccc',
          }}
          onClick={hasCopiedContent ? handlePaste : undefined}
          onMouseEnter={(e) => {
            if (hasCopiedContent) {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          粘贴
        </li>
        
        {/* 删除选项：仅在有选择内容且右键点击在选中区域内时可用 */}
        {showCopyDeleteOptions && (
          <li
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              transition: 'background-color 0.2s',
              userSelect: 'none',
              color: '#e74c3c' // 红色字体
            }}
            onClick={handleDelete}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            删除
          </li>
        )}
        
        {/* 组合选项：在以下情况显示：
            1. 有多个元素被选中且不在组内
            2. 同时选中了组内元素和组外元素
            3. 选中了多个组合 */}
        {showCopyDeleteOptions && canGroup && (hasSelectionOutsideGroup || !isInGroup || hasMultipleGroupsSelected) && (
          <li
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              transition: 'background-color 0.2s',
              userSelect: 'none'
            }}
            onClick={handleGroup}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            组合
          </li>
        )}
        
        {/* 解除组合选项：仅在组内右键点击且没有选中组外元素且没有选中多个组合时显示 */}
        {isInGroup && !hasSelectionOutsideGroup && !hasMultipleGroupsSelected && (
          <li
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              transition: 'background-color 0.2s',
              userSelect: 'none'
            }}
            onClick={handleUngroup}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            解除组合
          </li>
        )}
        
        {/* 全部选中选项：仅在空菜单中显示 */}
        {type === 'empty' && (
          <li
            style={{
              padding: '8px 16px',
              cursor: isContentEmpty ? 'not-allowed' : 'pointer',
              backgroundColor: 'transparent',
              transition: 'background-color 0.2s',
              userSelect: 'none',
              color: isContentEmpty ? '#bbb' : 'inherit',
              opacity: isContentEmpty ? 0.7 : 1
            }}
            onClick={isContentEmpty ? undefined : handleSelectAll}
            onMouseEnter={(e) => {
              if (!isContentEmpty) {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            全部选中
          </li>
        )}
        
        {/* 添加注释选项：仅在文本上下文菜单中显示 */}
        {type === 'text' && textIndex !== undefined && (
          <li
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              transition: 'background-color 0.2s',
              userSelect: 'none'
            }}
            onClick={handleAddAnnotation}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {textService.getTexts()[textIndex]?.hasAnnotation ? '编辑注释' : '添加注释'}
          </li>
        )}
      </ul>
    </div>
  );
};

export default ContextMenu; 